import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import ScoreBadge from "../components/ScoreBadge";
import LeadDrawer from "../components/LeadDrawer";
import { toast } from "sonner";

const STAGES = [
  { id: "new", label: "New" },
  { id: "enriched", label: "Enriched" },
  { id: "personalized", label: "Personalized" },
  { id: "in_sequence", label: "In Sequence" },
  { id: "engaged", label: "Engaged" },
  { id: "qualified", label: "Qualified" },
  { id: "meeting_booked", label: "Meeting Booked" },
  { id: "meeting_held", label: "Meeting Held" },
  { id: "opportunity", label: "Opportunity" },
  { id: "client_won", label: "Client Won" },
  { id: "nurture", label: "Nurture" },
];

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [focus, setFocus] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch {
      toast.error("Failed to load pipeline");
    }
  };

  useEffect(() => { load(); }, []);

  const moveTo = async (lead_id, status) => {
    try {
      const { data } = await api.patch(`/leads/${lead_id}`, { status });
      setLeads((prev) => prev.map((l) => (l.lead_id === lead_id ? data : l)));
    } catch {
      toast.error("Failed to move lead");
    }
  };

  return (
    <Layout title="Pipeline" subtitle="Kanban">
      <div className="overflow-x-auto -mx-8 px-8 pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map((stage) => {
            const items = leads.filter((l) => l.status === stage.id);
            return (
              <div
                key={stage.id}
                className="w-[280px] shrink-0 bg-secondary/40 border border-border"
                style={{ borderRadius: 2 }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (draggingId) {
                    moveTo(draggingId, stage.id);
                    setDraggingId(null);
                  }
                }}
                data-testid={`column-${stage.id}`}
              >
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="overline">{stage.label}</div>
                  <div className="font-mono text-xs">{items.length}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[200px]">
                  {items.map((l) => (
                    <div
                      key={l.lead_id}
                      draggable
                      onDragStart={() => setDraggingId(l.lead_id)}
                      onClick={() => setFocus(l)}
                      className="bg-card border border-border p-3 cursor-grab lift fade-up"
                      style={{ borderRadius: 2 }}
                      data-testid={`card-${l.lead_id}`}
                    >
                      <div className="font-medium text-sm truncate">{l.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{l.company} · {l.industry}</div>
                      <div className="mt-2"><ScoreBadge score={l.score} band={l.score_band} /></div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <LeadDrawer
        lead={focus}
        onClose={() => setFocus(null)}
        onUpdated={(updated) => {
          setFocus(updated);
          setLeads((prev) => prev.map((x) => (x.lead_id === updated.lead_id ? updated : x)));
        }}
      />
    </Layout>
  );
}
